import { config } from '../config/config';

// Normalize a Lead document into the same shape the permit forms expect from a
// Project. Permit prefill code only looks at: name, client, address, jobNumber.
const adaptLeadToProjectShape = (lead) => {
  if (!lead) return lead;

  const contactName = lead.leadContact?.name || '';

  // Lead.client populates with a single `name` field; the prefill helpers
  // expect either companyName or firstName/lastName. Bridge both shapes.
  let client;
  if (lead.client && typeof lead.client === 'object') {
    const raw = lead.client;
    const rawName = raw.name || '';
    const parts = rawName.split(' ');
    client = {
      ...raw,
      companyName: raw.companyName || rawName,
      firstName: raw.firstName || parts[0] || '',
      lastName: raw.lastName || parts.slice(1).join(' ') || ''
    };
  } else {
    const parts = contactName.split(' ');
    client = {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
      companyName: contactName,
      email: lead.leadContact?.email || ''
    };
  }

  return {
    _id: lead._id,
    name: lead.leadContact?.name
      ? `${lead.jobNumber ? `#${lead.jobNumber} — ` : ''}${lead.leadContact.name}`
      : (lead.jobNumber ? `Lead #${lead.jobNumber}` : 'Lead'),
    jobNumber: lead.jobNumber || '',
    address: lead.address || {},
    client,
    originalLead: lead
  };
};

/**
 * Fetches owner data for prefilling permit forms. Accepts either a Project ID
 * or a Lead ID. Tries `/api/projects/:id` first and falls back to
 * `/api/leads/:id` (shaping the lead into the project-like structure forms
 * expect) so permit forms work in both project and lead contexts.
 *
 * @param {string} ownerId - The ID of the project or lead to fetch
 * @returns {Promise<Object>} - The project (or lead-as-project) data
 */
export const fetchProjectData = async (ownerId) => {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  try {
    const projectRes = await fetch(`${config.apiUrl}/api/projects/${ownerId}`, { headers });
    if (projectRes.ok) {
      const data = await projectRes.json();
      return data.project || data;
    }

    // Fall back to leads only on 404 (not-found), so we don't mask real errors.
    if (projectRes.status !== 404) {
      throw new Error('Failed to fetch project data');
    }
  } catch (err) {
    // Network/parsing failure — try leads endpoint as a fallback.
    console.warn('Project fetch failed, attempting lead fallback:', err);
  }

  try {
    const leadRes = await fetch(`${config.apiUrl}/api/leads/${ownerId}`, { headers });
    if (!leadRes.ok) {
      throw new Error('Failed to fetch lead data');
    }
    const leadData = await leadRes.json();
    const lead = leadData.lead || leadData;
    return adaptLeadToProjectShape(lead);
  } catch (error) {
    console.error('Error fetching owner data:', error);
    throw error;
  }
};

/**
 * Extracts client name from client object
 * 
 * @param {Object} client - The client object
 * @returns {string} - The formatted client name
 */
export const getClientName = (client) => {
  if (!client) return '';
  
  if (client.companyName) {
    return client.companyName;
  } else {
    return `${client.firstName || ''} ${client.lastName || ''}`.trim();
  }
};

/**
 * Gets formatted project address
 * 
 * @param {Object} project - The project object
 * @returns {string} - The formatted project address
 */
export const getProjectAddress = (project) => {
  if (!project || !project.address) return '';
  
  const { street, city, state, zipCode } = project.address;
  const parts = [street, city, state, zipCode].filter(Boolean);
  return parts.join(', ');
};

/**
 * Prefills form data with project information
 * 
 * @param {Object} project - The project object
 * @param {Object} formData - The current form data
 * @returns {Object} - The updated form data
 */
export const prefillFormData = (project, formData) => {
  if (!project) return formData;
  
  // Get client name
  const clientName = project.client ? getClientName(project.client) : '';
  
  // Get project address
  const projectAddress = getProjectAddress(project);
  
  // Get project number - try various fields that might contain the job number
  let projectNumber = '';
  
  console.log('Looking for job number in project:', project);
  
  // Check common fields that might contain job number
  if (project.jobNumber) {
    projectNumber = project.jobNumber;
    console.log('Found jobNumber:', projectNumber);
  }
  else if (project.projectNumber) {
    projectNumber = project.projectNumber;
    console.log('Found projectNumber:', projectNumber);
  }
  else if (project.number) {
    projectNumber = project.number;
    console.log('Found number:', projectNumber);
  }
  else if (project._id) {
    projectNumber = project._id;
    console.log('Using _id as fallback:', projectNumber);
  }
  
  // If project has a lead or originalLead, check those fields too
  if (!projectNumber && project.lead) {
    console.log('Checking lead for job number:', project.lead);
    if (project.lead.jobNumber) projectNumber = project.lead.jobNumber;
    else if (project.lead.number) projectNumber = project.lead.number;
    else if (project.lead.leadNumber) projectNumber = project.lead.leadNumber;
    else if (project.lead._id) projectNumber = project.lead._id;
  }
  
  if (!projectNumber && project.originalLead) {
    console.log('Checking originalLead for job number:', project.originalLead);
    if (project.originalLead.jobNumber) projectNumber = project.originalLead.jobNumber;
    else if (project.originalLead.number) projectNumber = project.originalLead.number;
    else if (project.originalLead.leadNumber) projectNumber = project.originalLead.leadNumber;
    else if (project.originalLead._id) projectNumber = project.originalLead._id;
  }
  
  console.log('Final extracted project number:', projectNumber);
  
  // Get today's date in YYYY-MM-DD format for form inputs using local timezone
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(now.getDate()).padStart(2, '0');
  const today = `${year}-${month}-${day}`;
  
  // Create a new form data object with prefilled values
  const updatedFormData = {
    ...formData
  };
  
  // Update different field names based on what's available in the form
  if ('customer' in formData) {
    updatedFormData.customer = clientName;
  }
  
  if ('projectName' in formData) {
    updatedFormData.projectName = project.name || '';
  }
  
  if ('jobName' in formData) {
    updatedFormData.jobName = project.name || '';
  }
  
  if ('jobAddress' in formData) {
    updatedFormData.jobAddress = projectAddress;
  }
  
  if ('jobNumber' in formData) {
    updatedFormData.jobNumber = projectNumber;
  }
  
  // Set today's date for any date field
  if ('date' in formData) {
    updatedFormData.date = today;
  }
  
  return updatedFormData;
};
